import { useCallback, useMemo, useState } from "react";
import type { ChangeEvent, FocusEvent } from "react";

interface UseTrimmedInputOptions {
  initialValue?: string;
  onChange?: (value: string) => void;
  onBlurTrimmed?: (trimmedValue: string) => void;
  skipTrim?: boolean;
}

export const useTrimmedInput = ({
  initialValue = "",
  onChange,
  onBlurTrimmed,
  skipTrim = false,
}: UseTrimmedInputOptions = {}) => {
  const [inputState, setInputState] = useState(() => ({
    initialValue,
    value: initialValue,
  }));
  const value =
    inputState.initialValue === initialValue ? inputState.value : initialValue;
  const setValueInternal = useCallback(
    (value: string) => {
      setInputState({ initialValue, value });
    },
    [initialValue],
  );

  const setValue = useCallback(
    (newValue: string) => {
      setValueInternal(newValue);
      onChange?.(newValue);
    },
    [onChange, setValueInternal],
  );

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setValue(e.currentTarget.value);
    },
    [setValue],
  );

  const handleBlur = useCallback(
    (e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (skipTrim) {
        return;
      }
      const currentValue = e.currentTarget.value;
      const trimmed = currentValue.trim();
      if (trimmed !== currentValue) {
        setValueInternal(trimmed);
        onBlurTrimmed?.(trimmed);
      }
    },
    [onBlurTrimmed, setValueInternal, skipTrim],
  );

  const getTrimmedValue = useCallback(
    () => (skipTrim ? value : value.trim()),
    [skipTrim, value],
  );

  return useMemo(
    () => ({
      value,
      onChange: handleChange,
      onBlur: handleBlur,
      setValue,
      getTrimmedValue,
    }),
    [value, handleChange, handleBlur, setValue, getTrimmedValue],
  );
};
